import { z } from 'zod';
import { IsoDateTimeSchema, nullable } from './common.js';
import { HttpsUrlSchema } from './content.js';
import { CmsPageKeySchema, SocialPlatformSchema, VenueKeySchema } from './enums/venue.js';
/*
 * Venue profile and legal pages (M-05 Stadium, M-08 Terms and Privacy; Q-28 admin-editable):
 * 04-data-models.md § Venue and legal pages, 05-api-specification.md admin `/venue`,
 * `/cms-pages/{key}` and app `/venue`, `/pages/{key}`.
 */
export const VENUE_NAME_MAX_LENGTH = 120;
export const VENUE_ADDRESS_MAX_LENGTH = 300;
/** Sanitised HTML (TipTap); the stadium overview and the legal page bodies. */
export const VENUE_OVERVIEW_MAX_LENGTH = 20_000;
export const CMS_PAGE_TITLE_MAX_LENGTH = 120;
export const CMS_PAGE_BODY_MAX_LENGTH = 200_000;
/** A phone number as the Stadium screen shows it: digits, spaces, `+`, `(`, `)` and `-`. */
const ContactPhoneSchema = z
    .string()
    .trim()
    .regex(/^\+?[0-9 ()-]{6,30}$/, 'Digits, spaces, +, ( ) and - only (6 to 30 characters)');
export const VenueLocationSchema = z
    .object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
})
    .meta({ id: 'VenueLocation' });
export const VenueContactsSchema = z
    .object({
    landline: nullable(z.string()),
    whatsapp: nullable(z.string()),
    email: nullable(z.string()),
    postalCode: nullable(z.string()),
})
    .meta({ id: 'VenueContacts' });
export const VenueSocialLinkSchema = z
    .object({ platform: SocialPlatformSchema, url: HttpsUrlSchema })
    .meta({ id: 'VenueSocialLink' });
/**
 * The Stadium screens (Overview, Getting Here, Follow Us) and the admin Settings › Stadium form.
 * `overview` is sanitised HTML; `location` is the map pin.
 */
export const VenueSchema = z
    .object({
    key: VenueKeySchema,
    name: z.string(),
    overview: z.string(),
    address: z.string(),
    location: nullable(VenueLocationSchema),
    contacts: VenueContactsSchema,
    socialLinks: z.array(VenueSocialLinkSchema),
    updatedAt: nullable(IsoDateTimeSchema),
})
    .meta({ id: 'Venue' });
/** `PUT /v1/admin/venue`: replaces the whole profile; send `null` for an empty contact field. */
export const VenueUpdateRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(VENUE_NAME_MAX_LENGTH),
    overview: z.string().max(VENUE_OVERVIEW_MAX_LENGTH),
    address: z.string().trim().min(1).max(VENUE_ADDRESS_MAX_LENGTH),
    location: VenueLocationSchema.nullable(),
    contacts: z.strictObject({
        landline: ContactPhoneSchema.nullable(),
        whatsapp: ContactPhoneSchema.nullable(),
        email: z.email().max(254).nullable(),
        postalCode: z
            .string()
            .trim()
            .regex(/^[0-9A-Za-z -]{3,12}$/, 'Invalid postal code')
            .nullable(),
    }),
    socialLinks: z
        .array(z.strictObject({ platform: SocialPlatformSchema, url: HttpsUrlSchema }))
        .max(10)
        .refine((links) => new Set(links.map((link) => link.platform)).size === links.length, {
        message: 'One link per platform',
    }),
});
/** One published version of a legal page (admin version history). */
export const CmsPageVersionSchema = z
    .object({
    version: z.number().int().positive(),
    title: z.string(),
    publishedAt: IsoDateTimeSchema,
    /** Admin email at publish time; `null` for the seeded version. */
    publishedBy: nullable(z.string()),
})
    .meta({ id: 'CmsPageVersion' });
/** `GET /v1/app/pages/{key}` (public): the published Terms or Privacy Policy. */
export const AppCmsPageSchema = z
    .object({
    key: CmsPageKeySchema,
    title: z.string(),
    /** Sanitised HTML; render with a native HTML renderer. */
    body: z.string(),
    version: z.number().int().positive(),
    publishedAt: IsoDateTimeSchema,
})
    .meta({ id: 'AppCmsPage' });
/** `GET` and `PUT /v1/admin/cms-pages/{key}`: the current version plus the history, newest first. */
export const CmsPageSchema = AppCmsPageSchema.extend({
    history: z.array(CmsPageVersionSchema),
}).meta({ id: 'CmsPage' });
/** `PUT /v1/admin/cms-pages/{key}`: publishes a new version (`version` + 1). */
export const CmsPagePublishRequestSchema = z.strictObject({
    title: z.string().trim().min(1).max(CMS_PAGE_TITLE_MAX_LENGTH),
    body: z.string().min(1).max(CMS_PAGE_BODY_MAX_LENGTH),
});
/**
 * `meta` of the 409 `CONFLICT` from `POST /v1/app/auth/signup/otp` when the accepted versions are
 * not the published ones: show the Terms and Privacy links again and resend with these versions.
 */
export const LegalVersionsMetaSchema = z
    .object({
    termsVersion: z.number().int().positive(),
    privacyVersion: z.number().int().positive(),
})
    .meta({ id: 'LegalVersionsMeta' });
//# sourceMappingURL=venue.js.map